import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { SearchIcon } from "./icons";
import { VisuallyHidden } from "radix-ui";

import { cn } from "./utils";
import { Dialog, DialogContent, DialogTitle } from "./dialog";

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  // Flat — the parent surface (DialogContent in CommandDialog, or a Popover
  // when used inline) already provides the elevated bg and shadow.
  return (
    <CommandPrimitive
      className={cn(
        "flex h-full w-full flex-col overflow-hidden rounded-md text-foreground",
        className,
      )}
      {...props}
    />
  );
}

function CommandDialog({
  children,
  shouldFilter,
  filter,
  ...props
}: React.ComponentProps<typeof Dialog> &
  Pick<React.ComponentProps<typeof CommandPrimitive>, "shouldFilter" | "filter">) {
  return (
    <Dialog {...props}>
      <DialogContent
        className="overflow-hidden p-0 max-w-xl"
        showCloseButton={false}
      >
        <VisuallyHidden.Root>
          <DialogTitle>Command Palette</DialogTitle>
        </VisuallyHidden.Root>
        {/* The forced [&_[cmdk-item]]:py-2.5 is THE deliberate palette exception
            to the py-1.5 floating-menu row rhythm (PRD T3) — the full-screen
            command palette gets roomier rows than an inline menu. shouldFilter/
            filter pass through for a caller doing its own server/JS filtering
            (e.g. a value that isn't the display label) — default undefined
            keeps cmdk's own built-in fuzzy filter, unchanged for existing callers. */}
        <Command
          shouldFilter={shouldFilter}
          filter={filter}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:type-body [&_[cmdk-group-heading]]:text-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-4 [&_[cmdk-input-wrapper]_svg]:w-4 [&_[cmdk-input]]:h-11 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5"
        >
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div
      className="flex items-center border-b border-border px-3"
      cmdk-input-wrapper=""
    >
      <SearchIcon className="mr-2 h-4 w-4 shrink-0 text-foreground" />
      <CommandPrimitive.Input
        className={cn(
          "flex h-11 w-full rounded-md bg-transparent py-3 type-body outline-none placeholder:text-foreground disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    </div>
  );
}

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      className={cn(
        "max-h-[400px] overflow-y-auto overflow-x-hidden",
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      className="py-8 text-center type-body text-foreground"
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      className={cn(
        "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:type-body [&_[cmdk-group-heading]]:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      className={cn("-mx-1 h-px bg-border", className)}
      {...props}
    />
  );
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      className={cn(
        // py-1.5 = the one floating-menu item rhythm, matching Dropdown/Context/
        // Select items (PRD T3). CommandDialog overrides it back to py-2.5 as the
        // deliberate command-palette exception (see CommandDialog below).
        "relative flex cursor-pointer gap-2 select-none items-center rounded-sm px-2 py-1.5 type-body outline-none",
        "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        "data-[selected=true]:bg-muted data-[selected=true]:text-foreground",
        "hover:bg-muted/60",
        className,
      )}
      {...props}
    />
  );
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "ml-auto type-body tracking-widest text-foreground uppercase",
        className,
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
